import { ClaimApiClientTicketResponse } from "@somewear/api";
import { grpc, someGrpc } from "@somewear/grpc";
import { useCallback, useState } from "react";
import { take } from "rxjs/operators";

import { useAuthSessionBounce } from "./useAuthSessionBounce";

export const TICKET_AUTH_SESSION_KEY = "api_client_ticket";
const SESSION_KEY_APP_CLIENT_ID = "api_client_issuing_id";

export interface ClaimableWorkspace {
	id: string;
	name: string;
	isDefault: boolean;
}

export enum TicketAuthStage {
	/** No ticket in the URL, or waiting for the user to sign in */
	Idle = "idle",
	/** Session bounce or claim request in flight */
	Claiming = "claiming",
	SelectingOrg = "selecting-org",
	SelectingWorkspaces = "selecting-workspaces",
	Done = "done",
	/** The ticket outlived its server-side TTL; the link can't be reused */
	Expired = "expired",
	Error = "error",
}

export interface TicketAuthResult {
	stage: TicketAuthStage;
	/** Workspaces of the chosen organization, populated for SelectingWorkspaces */
	workspaces: ClaimableWorkspace[];
	/** Populated for Error */
	error: string | null;
	onOrgSelected: (orgId: string) => void;
	onWorkspacesSelected: (workspaceIds: string[]) => void;
}

/**
 * Handles the ticket-based auth flow (SSH-safe alternative to the callback_port
 * redirect). When the app is opened with ?auth_ticket=<ticket>&issuing_app_client_id=<id>:
 * - Signs out any existing session (via useAuthSessionBounce)
 * - Waits for a fresh sign-in (sign-in form renders normally during this phase)
 * - Calls ClaimApiClientTicket once auth lands
 * - If the user belongs to multiple orgs, surfaces an org picker; re-calls with the selection
 * - Surfaces a workspace picker for the chosen org; re-calls with the selected workspace ids
 * - Ends in Done on success, Expired when the link is stale, Error on failure
 */
export function useTicketAuth(
	authServiceLoaded: boolean,
	hasSession: boolean | undefined,
	activeAuthId: string | undefined,
): TicketAuthResult {
	const [stage, setStage] = useState(TicketAuthStage.Idle);
	const [selectedOrgId, setSelectedOrgId] = useState<string | undefined>(undefined);
	const [workspaces, setWorkspaces] = useState<ClaimableWorkspace[]>([]);
	const [error, setError] = useState<string | null>(null);

	const finish = useCallback((stage: TicketAuthStage, message: string | null = null) => {
		sessionStorage.removeItem(TICKET_AUTH_SESSION_KEY);
		sessionStorage.removeItem(SESSION_KEY_APP_CLIENT_ID);
		setError(message);
		setStage(stage);
	}, []);

	const claim = useCallback(
		(orgId?: string, workspaceIds?: string[]) => {
			const ticket = sessionStorage.getItem(TICKET_AUTH_SESSION_KEY);
			const issuingAppClientId = sessionStorage.getItem(SESSION_KEY_APP_CLIENT_ID);
			if (!ticket || !issuingAppClientId) return;

			setStage(TicketAuthStage.Claiming);
			grpc.prepareRequestWithPayload(someGrpc.claimApiClientTicket, {
				ticket,
				issuingAppClientId,
				organizationId: orgId,
				workspaceIds,
			})
				.pipe(take(1))
				.subscribe({
					next: (response) => {
						switch (response.getResult()) {
							case ClaimApiClientTicketResponse.Result.SUCCESS:
								finish(TicketAuthStage.Done);
								break;
							case ClaimApiClientTicketResponse.Result.ORG_SELECTION_REQUIRED:
								setStage(TicketAuthStage.SelectingOrg);
								break;
							case ClaimApiClientTicketResponse.Result.WORKSPACE_SELECTION_REQUIRED:
								setWorkspaces(
									response.getWorkspacesList().map((w) => w.toObject()),
								);
								setStage(TicketAuthStage.SelectingWorkspaces);
								break;
							default:
								finish(TicketAuthStage.Expired);
						}
					},
					error: (err) => {
						console.error("useTicketAuth: claimApiClientTicket failed", err);
						finish(TicketAuthStage.Error, "Authorization failed. Please try again.");
					},
				});
		},
		[finish],
	);

	const onToken = useCallback(
		(_tokenString: string) => {
			claim();
		},
		[claim],
	);

	const onOrgSelected = useCallback(
		(orgId: string) => {
			setSelectedOrgId(orgId);
			claim(orgId);
		},
		[claim],
	);

	const onWorkspacesSelected = useCallback(
		(workspaceIds: string[]) => {
			claim(selectedOrgId, workspaceIds);
		},
		[claim, selectedOrgId],
	);

	const bouncing = useAuthSessionBounce(
		() => {
			const params = new URLSearchParams(window.location.search);
			const ticket = params.get("auth_ticket");
			if (!ticket) return null;
			const issuingAppClientId = params.get("issuing_app_client_id");
			if (!issuingAppClientId) return null;

			sessionStorage.setItem(TICKET_AUTH_SESSION_KEY, ticket);
			sessionStorage.setItem(SESSION_KEY_APP_CLIENT_ID, issuingAppClientId);

			// A ticket is single-use; drop it from the URL so a sign-out or reload can't replay it.
			params.delete("auth_ticket");
			params.delete("issuing_app_client_id");
			const search = params.toString();
			window.history.replaceState(
				null,
				"",
				`${window.location.pathname}${search ? `?${search}` : ""}`,
			);
			return ticket;
		},
		false, // don't force sign-out — any authenticated session can claim a ticket
		authServiceLoaded,
		hasSession,
		activeAuthId,
		onToken,
	);

	return {
		stage: stage === TicketAuthStage.Idle && bouncing ? TicketAuthStage.Claiming : stage,
		workspaces,
		error,
		onOrgSelected,
		onWorkspacesSelected,
	};
}
